// Abstract PayrollEngine base class - defines the contract for all country-specific implementations

import { Employee } from '@/types';
import {
  CountryCode,
  PayrollInputData,
  PayrollCalculation,
  CountryPayrollConstants,
  GrossPayComponents,
  InsuranceCalculation,
  TaxCalculation,
  DeductionCalculation,
  PayrollEngineError,
  ValidationError,
  CalculationError
} from './types';

/**
 * Abstract base class for all payroll calculation engines
 * Each country should extend this class and implement country-specific logic
 */
export abstract class PayrollEngine {
  protected readonly countryCode: CountryCode;
  protected constants: CountryPayrollConstants | null = null;
  protected debugMode: boolean = false;

  constructor(countryCode: CountryCode, debugMode: boolean = false) {
    this.countryCode = countryCode;
    this.debugMode = debugMode;
  }

  /**
   * Main entry point for payroll calculation
   * This method orchestrates the entire calculation process
   */
  async calculatePayroll(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<PayrollCalculation> {
    try {
      const calculationType = inputData.isPlanning ? 'planning' : 'actual';
      this.log(`Starting ${calculationType} payroll calculation for employee ${employee.name} (${employee.id}) in ${this.countryCode}`);
      
      // Step 1: Load country-specific constants
      this.constants = await this.loadPayrollConstants(inputData.period.year);
      
      // Step 2: Validate input data
      await this.validateInput(employee, inputData);
      
      // Step 3: Process hours based on mode (planning vs actual)
      const processedInputData = this.processHoursForCalculation(employee, inputData);
      
      // Step 4: Calculate gross pay components
      const grossComponents = await this.calculateGrossComponents(employee, processedInputData);
      this.log(`Gross components calculated: ${JSON.stringify(grossComponents)}`);
      
      // Step 5: Calculate insurance contributions
      const insuranceCalculations = await this.calculateInsuranceContributions(employee, grossComponents, processedInputData);
      this.log(`Insurance calculations completed`);
      
      // Step 6: Calculate tax
      const taxCalculation = await this.calculateTax(employee, grossComponents, insuranceCalculations, processedInputData);
      this.log(`Tax calculation completed: ${JSON.stringify(taxCalculation)}`);
      
      // Step 7: Calculate other deductions
      const deductions = await this.calculateOtherDeductions(employee, grossComponents, processedInputData);
      this.log(`Other deductions calculated: ${deductions.length} items`);
      
      // Step 7: Build final calculation result
      const result = await this.buildCalculationResult(
        employee,
        inputData,
        grossComponents,
        insuranceCalculations,
        taxCalculation,
        deductions
      );
      
      this.log(`Payroll calculation completed successfully for ${employee.name}`);
      return result;
      
    } catch (error) {
      this.log(`Error in payroll calculation: ${error instanceof Error ? error.message : 'Unknown error'}`);
      if (error instanceof PayrollEngineError) {
        throw error;
      }
      throw new CalculationError(
        this.countryCode,
        `Payroll calculation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'main_calculation',
        { employee: employee.id, inputData }
      );
    }
  }

  /**
   * Abstract methods - must be implemented by country-specific engines
   */

  /**
   * Load country-specific payroll constants for the given year
   */
  protected abstract loadPayrollConstants(year: number): Promise<CountryPayrollConstants>;

  /**
   * Calculate gross pay components based on time worked and rates
   */
  protected abstract calculateGrossComponents(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<GrossPayComponents>;

  /**
   * Calculate insurance contributions (social, health, etc.)
   */
  protected abstract calculateInsuranceContributions(
    employee: Employee,
    grossComponents: GrossPayComponents,
    inputData: PayrollInputData
  ): Promise<Record<string, InsuranceCalculation>>;

  /**
   * Calculate income tax and related deductions
   */
  protected abstract calculateTax(
    employee: Employee,
    grossComponents: GrossPayComponents,
    insuranceCalculations: Record<string, InsuranceCalculation>,
    inputData: PayrollInputData
  ): Promise<TaxCalculation>;

  /**
   * Calculate other deductions (meal vouchers, advances, etc.)
   */
  protected abstract calculateOtherDeductions(
    employee: Employee,
    grossComponents: GrossPayComponents,
    inputData: PayrollInputData
  ): Promise<DeductionCalculation[]>;

  /**
   * Get the hourly rate for the employee
   * This can be overridden by country-specific logic
   */
  protected getHourlyRate(employee: Employee): number {
    this.log(`Getting hourly rate for employee ${employee.name} (${employee.id})`);
    
    // Priority: Direct hourly_rate > Role pay rate > Minimum wage
    if (employee.hourly_rate && employee.hourly_rate > 0) {
      this.log(`Using direct hourly rate: €${employee.hourly_rate}/hour`);
      return employee.hourly_rate;
    }
    
    if (employee.roleInfo?.default_pay_rate && employee.roleInfo.default_pay_rate > 0) {
      let baseRate = employee.roleInfo.default_pay_rate;
      const adjustment = employee.salary_adjustment || 0;
      
      // Convert annual salary to hourly if needed
      if (employee.roleInfo.pay_type === 'salary') {
        baseRate = baseRate / 2080; // 40 hours × 52 weeks
        this.log(`Converting role salary to hourly: €${employee.roleInfo.default_pay_rate}/year → €${baseRate.toFixed(2)}/hour`);
      }
      
      const finalRate = Math.max(0, baseRate + adjustment);
      this.log(`Using role-based rate: €${finalRate.toFixed(2)}/hour (adjustment: €${adjustment})`);
      return finalRate;
    }
    
    // Fallback to minimum wage
    const minimumWage = this.constants?.minimumWageHourly || 0;
    this.log(`Using minimum wage fallback: €${minimumWage}/hour`);
    return minimumWage;
  }

  /**
   * Validate input data before calculation
   * Can be extended by country-specific implementations
   */
  protected async validateInput(employee: Employee, inputData: PayrollInputData): Promise<void> {
    const errors: Record<string, string[]> = {};
    
    // Basic validation
    if (!employee.id) {
      errors.employee = ['Employee ID is required'];
    }
    
    if (inputData.regularHours < 0) {
      errors.regularHours = ['Regular hours cannot be negative'];
    }
    
    if (inputData.overtimeHours < 0) {
      errors.overtimeHours = ['Overtime hours cannot be negative'];
    }
    
    // Country-specific validation
    const countryValidationErrors = await this.validateCountrySpecificData(employee, inputData);
    Object.assign(errors, countryValidationErrors);
    
    if (Object.keys(errors).length > 0) {
      throw new ValidationError(
        this.countryCode,
        'Input validation failed',
        errors,
        { employee: employee.id, inputData }
      );
    }
  }

  /**
   * Country-specific validation - can be overridden
   */
  protected async validateCountrySpecificData(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<Record<string, string[]>> {
    return {};
  }

  /**
   * Build the final calculation result
   */
  protected async buildCalculationResult(
    employee: Employee,
    inputData: PayrollInputData,
    grossComponents: GrossPayComponents,
    insuranceCalculations: Record<string, InsuranceCalculation>,
    taxCalculation: TaxCalculation,
    deductions: DeductionCalculation[]
  ): Promise<PayrollCalculation> {
    
    // Calculate totals
    const totalInsuranceEmployee = Object.values(insuranceCalculations)
      .reduce((sum, calc) => sum + calc.employeeTotal, 0);
    
    const totalInsuranceEmployer = Object.values(insuranceCalculations)
      .reduce((sum, calc) => sum + calc.employerTotal, 0);
    
    const totalOtherDeductions = deductions
      .reduce((sum, deduction) => sum + deduction.amount, 0);
    
    const netSalary = grossComponents.total 
      - totalInsuranceEmployee 
      - taxCalculation.totalTax 
      + taxCalculation.netTaxBenefit;
    
    const finalPayout = netSalary - totalOtherDeductions;
    const totalEmployerCost = grossComponents.total + totalInsuranceEmployer;
    
    return {
      employee,
      period: inputData.period,
      inputData,
      grossComponents,
      insuranceCalculations,
      taxCalculation,
      deductions,
      grossSalary: grossComponents.total,
      netSalary,
      finalPayout,
      totalEmployerCost,
      calculationDate: new Date().toISOString(),
      calculationEngine: this.constructor.name,
      countrySpecificData: {}
    };
  }

  /**
   * Utility methods
   */

  /**
   * Process hours for calculation based on planning vs actual mode
   */
  protected processHoursForCalculation(employee: Employee, inputData: PayrollInputData): PayrollInputData {
    if (!inputData.isPlanning) {
      // Use actual hours - no processing needed
      return inputData;
    }

    // Planning mode: use planned hours and adjust for salary employee compliance
    const processedData = { ...inputData };
    
    // Use planned hours if available, otherwise fallback to actual hours
    if (inputData.plannedRegularHours !== undefined) {
      processedData.regularHours = inputData.plannedRegularHours;
    }
    if (inputData.plannedOvertimeHours !== undefined) {
      processedData.overtimeHours = inputData.plannedOvertimeHours;
    }
    if (inputData.plannedSpecialHours) {
      processedData.specialHours = { ...inputData.plannedSpecialHours };
    }

    // For salary/fixed contract employees: handle over/under-scheduling
    if ((employee.roleInfo?.pay_type === 'salary' || employee.roleInfo?.pay_type === 'fixed_contract') 
        && inputData.contractedHoursPerMonth) {
      const totalPlannedHours = processedData.regularHours + processedData.overtimeHours;
      const contractedHours = inputData.contractedHoursPerMonth;
      
      if (totalPlannedHours > contractedHours) {
        // Over-scheduled: split into regular (up to contracted) + overtime (excess)
        processedData.regularHours = contractedHours;
        processedData.overtimeHours = totalPlannedHours - contractedHours;
        
        this.log(`Planning mode: Employee over-scheduled by ${processedData.overtimeHours} hours`);
      } else {
        // Under-scheduled or compliant: all hours are regular, no overtime
        processedData.regularHours = totalPlannedHours;
        processedData.overtimeHours = 0;
        
        if (totalPlannedHours < contractedHours) {
          this.log(`Planning mode: Employee under-scheduled by ${contractedHours - totalPlannedHours} hours`);
        }
      }
    }

    this.log(`Planning mode hours processed: regular=${processedData.regularHours}, overtime=${processedData.overtimeHours}`);
    return processedData;
  }

  /**
   * Calculate planning compliance for salary/fixed contract employees
   */
  protected calculatePlanningCompliance(
    employee: Employee, 
    plannedHours: number, 
    contractedHours?: number
  ): {
    status: 'compliant' | 'over_scheduled' | 'under_scheduled' | 'not_applicable';
    variance: number;
    warnings: string[];
  } {
    const warnings: string[] = [];
    
    if (!contractedHours || !['salary', 'fixed_contract'].includes(employee.roleInfo?.pay_type || '')) {
      return { status: 'not_applicable', variance: 0, warnings };
    }

    const variance = plannedHours - contractedHours;
    const tolerance = 4; // 4 hour tolerance for under-scheduling
    
    if (variance > 0) {
      warnings.push(`Scheduled ${variance.toFixed(1)} hours over contracted amount (${contractedHours} hours/month)`);
      warnings.push(`Overtime pay will apply to ${variance.toFixed(1)} excess hours`);
      return { status: 'over_scheduled', variance, warnings };
    } else if (variance < -tolerance) {
      const underScheduled = Math.abs(variance);
      warnings.push(`Scheduled ${underScheduled.toFixed(1)} hours under contracted amount (${contractedHours} hours/month)`);
      warnings.push(`Employee will still receive full salary despite being under-scheduled`);
      return { status: 'under_scheduled', variance, warnings };
    } else {
      return { status: 'compliant', variance, warnings };
    }
  }

  /**
   * Get effective hours for gross pay calculation in planning mode
   */
  protected getEffectiveHoursForGrossPay(
    employee: Employee,
    inputData: PayrollInputData
  ): { regularHours: number; overtimeHours: number; basePayHours: number } {
    const payType = employee.roleInfo?.pay_type || 'hourly';
    
    if (payType === 'hourly') {
      // Hourly employees: pay exactly for hours worked/planned
      return {
        regularHours: inputData.regularHours,
        overtimeHours: inputData.overtimeHours,
        basePayHours: inputData.regularHours
      };
    }
    
    if (payType === 'salary' || payType === 'fixed_contract') {
      const contractedHours = inputData.contractedHoursPerMonth || 0;
      
      if (inputData.isPlanning && contractedHours > 0) {
        // Planning mode for salary: always pay for contracted hours minimum
        const basePayHours = Math.max(contractedHours, inputData.regularHours);
        const overtimeHours = Math.max(0, inputData.regularHours + inputData.overtimeHours - contractedHours);
        
        return {
          regularHours: inputData.regularHours,
          overtimeHours: overtimeHours,
          basePayHours: basePayHours
        };
      } else {
        // Actual mode for salary: pay based on actual work
        return {
          regularHours: inputData.regularHours,
          overtimeHours: inputData.overtimeHours,
          basePayHours: inputData.regularHours
        };
      }
    }
    
    return {
      regularHours: inputData.regularHours,
      overtimeHours: inputData.overtimeHours,
      basePayHours: inputData.regularHours
    };
  }

  /**
   * Log debug messages if debug mode is enabled
   */
  protected log(message: string, data?: any): void {
    if (this.debugMode) {
      console.log(`[${this.countryCode} PayrollEngine] ${message}`, data || '');
    }
  }

  /**
   * Get country code for this engine
   */
  getCountryCode(): CountryCode {
    return this.countryCode;
  }

  /**
   * Check if constants are loaded
   */
  protected ensureConstantsLoaded(): void {
    if (!this.constants) {
      throw new PayrollEngineError(
        this.countryCode,
        'CONSTANTS_NOT_LOADED',
        'Payroll constants must be loaded before calculation'
      );
    }
  }
}